import { useTranslation } from 'react-i18next';
import { useFixedParam } from '@/lib/scopedParams';
import { Link } from 'react-router';
import { api } from '../../api';
import type { PollStatusFilter, PollSummary } from '../../api/areas/registry';
import { DataTable, FilterBar, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import {
  CountBadge,
  enumLabel,
  FilterSelect,
  OpenCell,
  Pager,
  PollTable,
  RefreshButton,
  RegistryRef,
  RegistrySelect,
  ResetButton,
  useParamSetter,
  useRegistryCrumbs,
  When,
} from './shared';

const STATUS_FILTERS: PollStatusFilter[] = ['UNPROCESSED', 'UNACKED', 'ERROR', 'PROCESSED'];

/** `/registry/poll`: what the registries told us and whether it has been handled (spec §29). */
export function PollPage() {
  const { t } = useTranslation('registry');
  useDocumentTitle(t('poll.title'));
  const crumbs = useRegistryCrumbs();
  const [params, set, reset] = useParamSetter();
  const registry = params.get('registry') ?? '';
  const status = (params.get('status') ?? '') as PollStatusFilter | '';
  const before = params.get('before') ?? '';
  const result = useApi(
    (signal) => api.listPollMessages({ registry: registry || undefined, status: status || undefined, before: before || undefined, limit: 50 }, { signal }),
    [registry, status, before],
  );

  // On a registry's page the registry column and "open registry" would repeat the page.
  const oneRegistry = Boolean(useFixedParam('registry'));
  const filterLink = (reg: string, s: PollStatusFilter) => `/registry/poll?registry=${encodeURIComponent(reg)}&status=${s}`;
  const summaryColumns: Column<PollSummary>[] = [
    ...(oneRegistry ? [] : [{ key: 'registry', header: t('poll.registry'), cell: (s: PollSummary) => <RegistryRef name={s.registry_name} code={s.registry_code} /> }]),
    { key: 'total', header: t('poll.receivedCount'), numeric: true, cell: (s) => formatCount(s.total) },
    {
      key: 'unprocessed',
      header: t('poll.unprocessed'),
      numeric: true,
      cell: (s) => (s.unprocessed ? <Link to={filterLink(s.registry_code, 'UNPROCESSED')}><CountBadge n={s.unprocessed} tone="warn" /></Link> : <CountBadge n={0} tone="warn" />),
    },
    {
      key: 'unacked',
      header: t('poll.unacked'),
      numeric: true,
      cell: (s) => (s.unacked ? <Link to={filterLink(s.registry_code, 'UNACKED')}><CountBadge n={s.unacked} tone="warn" /></Link> : <CountBadge n={0} tone="warn" />),
    },
    {
      key: 'errors',
      header: t('poll.errors'),
      numeric: true,
      cell: (s) => (s.errors ? <Link to={filterLink(s.registry_code, 'ERROR')}><CountBadge n={s.errors} tone="crit" /></Link> : <CountBadge n={0} tone="crit" />),
    },
    { key: 'oldest', header: t('poll.oldest'), cell: (s) => <When at={s.oldest_unprocessed_at} /> },
    { key: 'last', header: t('poll.lastReceived'), cell: (s) => <When at={s.last_received_at} /> },
    ...(oneRegistry ? [] : [{
      key: 'go',
      header: <span className="sr-only">{t('poll.openRegistry')}</span>,
      className: 'w-8',
      cell: (s: PollSummary) => <OpenCell to={`/registry/registries/${encodeURIComponent(s.registry_code)}`} label={`${t('poll.openRegistry')}: ${s.registry_name}`} />,
    }]),
  ];

  return (
    <>
      <PageHeader title={t('poll.title')} crumbs={crumbs} subtitle={t('poll.subtitle')} actions={<RefreshButton onClick={result.reload} />} />
      <div className="space-y-6">
        {result.status === 'success' && result.data.summary.length > 0 ? (
          <Section flush title={t('poll.queue')}>
            <DataTable columns={summaryColumns} rows={result.data.summary} rowKey={(s) => s.registry_code} />
          </Section>
        ) : null}
        <div>
          <FilterBar actions={<ResetButton onClick={reset} disabled={!registry && !status} />}>
            <RegistrySelect value={registry} onChange={(v) => set('registry', v)} />
            <FilterSelect
              label={t('filter.status')}
              value={status}
              onChange={(v) => set('status', v)}
              className="min-w-44"
              options={STATUS_FILTERS.map((s) => ({ value: s, label: enumLabel('pollStatus', s) }))}
            />
          </FilterBar>
          <Section flush title={t('poll.messages')}>
            {result.status === 'loading' ? (
              <div className="px-5">
                <Loading rows={5} />
              </div>
            ) : null}
            {result.status === 'error' ? (
              <div className="px-5 pb-5">
                <DataError error={result.error} onRetry={result.reload} title={t('poll.loadError')} />
              </div>
            ) : null}
            {result.status === 'success' ? (
              <>
                <PollTable rows={result.data.items} empty={<EmptyState title={t('poll.empty')} />} />
                <Pager next={result.data.next_before} onNext={(c) => set('before', c)} first={!before} onFirst={() => set('before', '')} />
              </>
            ) : null}
          </Section>
        </div>
      </div>
    </>
  );
}
